import React from 'react';
import { ArrowRight, X, Check, Sparkles, Eye, Bot } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

const AEOSection = () => {
  const { t } = useTranslation();
  const beforeAfter = ['ba1', 'ba2', 'ba3', 'ba4'].map(id => ({
    id,
    before: t(`aeo.compare.${id}.before`),
    after: t(`aeo.compare.${id}.after`),
  }));

  const engines = [
    { name: 'ChatGPT',       subKey: 'openai',       color: '#10A37F' },
    { name: 'Claude',        subKey: 'anthropic',    color: '#D97706' },
    { name: 'Gemini',        subKey: 'googleAI',     color: '#4285F4' },
    { name: 'Perplexity',    subKey: 'answerEngine', color: '#20808D' },
    { name: 'Grok',          subKey: 'xai',          color: '#1DA1F2' },
    { name: 'Bing Copilot',  subKey: 'microsoft',    color: '#0078D4' },
    { name: 'Meta AI',       subKey: 'llama',        color: '#0866FF' },
    { name: 'DuckDuckGo AI', subKey: 'ddgSearch',    color: '#DE5833' },
  ];

  return (
    <section className="py-24 px-6 relative overflow-hidden" data-testid="aeo-section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0a14] to-[#050505]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B5CF6]/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B5CF6]/40 to-transparent" />
      <div className="absolute top-1/3 right-0 w-80 h-80 bg-[#8B5CF6]/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 left-0 w-64 h-64 bg-[#0055FF]/6 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Badge + Title */}
        <div className="text-center mb-16 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 mb-6">
            <Bot className="w-4 h-4 text-[#8B5CF6]" />
            <span className="text-sm text-[#c4b5fd]">{t('aeo.badge')}</span>
          </div>

          <h2 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-6">
            {t('aeo.title1')}
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#8B5CF6] to-[#EC4899]">
              {t('aeo.title2')}
            </span>
          </h2>

          <p className="text-lg text-[#A1A1AA] max-w-3xl mx-auto leading-relaxed mb-10">
            {t('aeo.subtitle')}
          </p>

          {/* 8 AI engines grid */}
          <div className="max-w-4xl mx-auto">
            <p className="text-xs uppercase tracking-[0.2em] text-[#8B5CF6] font-semibold mb-4">{t('aeo.enginesLabel')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {engines.map((eng) => (
                <div key={eng.name} className="rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 hover:border-white/20 transition-colors">
                  <div className="text-sm font-semibold text-white">{eng.name}</div>
                  <div className="text-[11px] text-[#A1A1AA] mt-0.5">{t(`aeo.engineSub.${eng.subKey}`)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Before / After Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16 animate-fade-up" style={{ animationDelay: '0.2s' }}>
          {/* Before Column */}
          <div className="rounded-2xl border border-[#EF4444]/20 bg-[#EF4444]/5 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#EF4444]/15 flex items-center justify-center">
                <X className="w-5 h-5 text-[#EF4444]" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-medium text-[#EF4444]">{t('aeo.withoutTitle')}</h3>
            </div>
            <div className="space-y-4">
              {beforeAfter.map((item) => (
                <div key={item.id} className="flex items-start gap-3 text-sm">
                  <X className="w-4 h-4 text-[#EF4444]/60 mt-0.5 flex-shrink-0" />
                  <span className="text-[#fca5a5]">{item.before}</span>
                </div>
              ))}
            </div>
          </div>

          {/* After Column */}
          <div className="rounded-2xl border border-[#10B981]/20 bg-[#10B981]/5 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#10B981]/15 flex items-center justify-center">
                <Check className="w-5 h-5 text-[#10B981]" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-medium text-[#10B981]">{t('aeo.withTitle')}</h3>
            </div>
            <div className="space-y-4">
              {beforeAfter.map((item) => (
                <div key={item.id} className="flex items-start gap-3 text-sm">
                  <Check className="w-4 h-4 text-[#10B981]/60 mt-0.5 flex-shrink-0" />
                  <span className="text-[#6ee7b7]">{item.after}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Explanatory Text */}
        <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-up" style={{ animationDelay: '0.3s' }}>
          <p
            className="text-base text-[#d4d4d8] leading-relaxed"
            dangerouslySetInnerHTML={{ __html: t('aeo.explanatory') }}
          />
        </div>

        {/* CTA */}
        <div className="text-center animate-fade-up" style={{ animationDelay: '0.4s' }}>
          <a href="#demo">
            <Button
              className="bg-[#8B5CF6] hover:bg-[#7c3aed] text-white rounded-full px-8 py-6 text-lg font-medium group"
              data-testid="aeo-cta-btn"
            >
              {t('aeo.ctaButton')}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
          <p className="mt-3 text-sm text-[#71717A]">{t('aeo.ctaNote')}</p>
        </div>
      </div>
    </section>
  );
};

export default AEOSection;
